import {
    ApplicationRef,
    ComponentRef,
    Directive,
    ElementRef,
    EnvironmentInjector,
    Input,
    Renderer2,
    createComponent,
    inject,
} from '@angular/core';
import { ComponentType } from 'src/boilerplate';
import { ErrorMessageComponent } from '../components/error-message/error-message.component';
import { ApiSignal } from '../util/api-signal';
import { LoadingIndicatorDirectiveBase } from './loading-indicator-base';

/**
 * A directive which will inject a centered sd-spinner as a child.
 * {@inheritDoc ApiCallDirectiveBase}
 */
@Directive({
  selector: '[wctLoadingIndicator], [wctApiCallInject]',
  standalone: true,
})
export class LoadingIndicatorDirective extends LoadingIndicatorDirectiveBase {
  /** The source for displayed errors and data. If the signal holds `undefined` the spinner will be visible. */
  @Input({ required: false }) public set wctApiCallInject(value: ApiSignal<unknown> | undefined) {
    this.apiSignal = value;
  }

  private injectedElement: HTMLElement | null = null;
  private injectedComponent: ComponentRef<unknown> | null = null;

  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly injector: EnvironmentInjector = inject(EnvironmentInjector);
  private readonly appRef: ApplicationRef = inject(ApplicationRef);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);

  protected override placeRegular(): void {
    return;
  }

  protected override placeError(error: Error): void {
    if (!this.apiCallDisplayErrorElement) {
      return;
    }

    this.renderer.addClass(this.elementRef.nativeElement, 'wct-relative');

    const el: HTMLElement = this.elementRef.nativeElement;
    const component: ComponentRef<ErrorMessageComponent> = this.createComponent(ErrorMessageComponent, el);
    const inj = component.location.nativeElement as HTMLElement;

    component.instance.errorMessage = this.apiCallErrorMsg || error.message;

    this.renderer.addClass(inj, 'wct-absolute-center');
    this.renderer.appendChild(el, inj);
    this.injectedComponent = component;
  }

  protected override placeLoading(): void {
    this.renderer.addClass(this.elementRef.nativeElement, 'wct-relative');

    const el: HTMLElement = this.elementRef.nativeElement;
    const inj: HTMLElement = document.createElement('sd-4-5-1-spinner');

    this.renderer.addClass(inj, 'wct-absolute-center');
    this.renderer.appendChild(el, inj);
    this.injectedElement = inj;
  }

  protected override clear(): void {
    if (this.injectedElement) {
      this.renderer.removeChild(this.elementRef.nativeElement, this.injectedElement);
      this.injectedElement = null;
    }
    if (this.injectedComponent) {
      this.injectedComponent.destroy();
      this.injectedComponent = null;
    }
  }

  private createComponent<T>(component: ComponentType<T>, parent: HTMLElement): ComponentRef<T> {
    const element: HTMLElement = document.createElement('div');

    const componentRef: ComponentRef<T> = createComponent(component, {
      environmentInjector: this.injector,
      hostElement: element,
    });

    this.appRef.attachView(componentRef.hostView);
    this.renderer.appendChild(parent, element);

    return componentRef;
  }
}
